/**
 * Central route constants — single source of truth for all navigation paths.
 *
 * Every page, component, hook, and call site MUST use these constants instead of
 * hardcoding route strings. Edge functions cannot import from `src/` but must
 * keep their literal strings in sync with this file (reference the constant
 * name in a code comment).
 */
export const ROUTES = {
  // Public
  HOME: '/',
  AUTH: '/auth',
  ONBOARDING_START: '/onboarding/start',
  ONBOARDING_ORG: '/onboarding/org',
  PRICING: '/pricing',
  INVITE: (token: string) => `/invite/${token}`,

  // App
  DASHBOARD: '/app/dashboard',
  SQUAD: '/app/squad',
  PLAYERS: '/app/players',
  PLAYER_DETAIL: (id: string) => `/app/players/${id}`,
  PERFORMANCE: '/app/performance',
  MATCHES: '/app/matches',
  MATCH_DETAIL: (id: string) => `/app/matches/${id}`,
  TRAINING: '/app/training',
  SESSIONS: '/app/sessions',
  PLANNED_SESSIONS: '/app/planned-sessions',
  PLANNED_SESSION_DETAIL: (id: string) => `/app/planned-sessions/${id}`,
  PLANNED_SESSION_EDIT: (id: string) => `/app/planned-sessions/${id}/edit`,
  PLANNING: '/app/planning',
  WELLNESS: '/app/wellness',
  WELLNESS_SUMMARY: '/app/wellness-summary',
  INJURIES: '/app/injuries',
  INJURY_TRACKING: '/app/injury-tracking',
  RTP: '/app/rtp',
  RTP_DETAIL: (id: string) => `/app/rtp/${id}`,
  RETURN_TO_PLAY: '/app/return-to-play',
  COLLABORATION: '/app/collaboration',
  AVAILABILITY: '/app/availability',
  INSIGHTS: '/app/insights',
  INSIGHTS_HUB: '/app/insights-hub',
  COACH_INSIGHTS: '/app/coach-insights',
  // AI_REPORTS, BRIEFING_ROOM, BRIEFING_ROOM_DETAIL retired — features removed
  WEEKLY_REPORT: '/app/weekly-report',
  TRAINING_PREVIEW: '/app/training-preview',
  RISK_MONITOR: '/app/risk-monitor',
  RISK_FORECAST: '/app/risk-forecast',
  // GUARDRAILS route retired — see WORKFLOWS for the replacement workflow templates.
  ANALYTICS: '/app/analytics',
  PULSE: '/app/pulse',
  CALENDAR: '/app/calendar',
  MESSAGING: '/app/messaging',
  DOCUMENTS: '/app/documents',
  MEMBERS: '/app/members',
  ACTIONS: '/app/actions',
  DRILL_LIBRARY: '/app/drill-library',
  VIDEO_ANALYSIS: '/app/video-analysis',
  TRAVEL: '/app/travel',
  COMPLIANCE: '/app/compliance',
  INTEGRATIONS: '/app/integrations',
  DIRECTOR_DASHBOARD: '/app/director-dashboard',
  STAFF_DIRECTORY: '/app/staff-directory',
  // PAYMENTS / SEASON_REGISTRATION / CAMPS_CLINICS / CAMPS / CAMP_REGISTRATIONS /
  // REGISTRATION / RECRUITMENT_OPPOSITION / OPPOSITION_ANALYSIS / SCOUTING /
  // SCOUTING_DETAIL / VENUES / DRILL_LIBRARY / VIDEO_ANALYSIS — all removed
  // from product scope.
  SEASON: '/app/season',
  SCENARIO_PLANNER: '/app/scenario-planner',
  SQUAD_GAPS: '/app/squad-gaps',
  COMPARE: '/app/compare',
  BLOCK_ANALYZER: '/app/block-analyzer',
  STAFF: '/app/staff',
  CLUB: '/app/club',
  BUSINESS: '/app/business',
  ADMIN: '/app/admin',
  CLUB_SETUP: '/app/club-setup',
  SUPER_ADMIN: '/app/super-admin',
  MEDICAL: '/app/medical',
  SQUAD_COMMS: '/app/squad-comms',
  SESSION_REVIEWS: '/app/session-reviews',
  TESTING_CALENDAR: '/app/testing-calendar',
  DATA_IMPORT: '/app/data-import',
  UPLOAD_HISTORY: '/app/upload-history',
  UPLOADS: '/app/uploads',
  UPLOAD_DETAIL: (id: string) => `/app/uploads/${id}`,
  UPLOAD_RESOLVE: (id: string) => `/app/uploads/${id}/resolve`,
  VALIDATION: '/app/validation',
  DATA_MANAGEMENT: '/app/data-management',

  // Workflows
  WORKFLOWS: '/app/workflows',
  WORKFLOWS_ALL: '/app/workflows/all',
  WORKFLOWS_TEMPLATES: '/app/workflows/templates',
  WORKFLOWS_NEW: '/app/workflows/new',
  WORKFLOW_DETAIL: (id: string) => `/app/workflows/${id}`,
  WORKFLOW_HISTORY: (id: string) => `/app/workflows/${id}/history`,

  // Matches (live mode)
  MATCH_BASE: '/app/match',
  MATCH_MODE: (id: string) => `/app/match/${id}`,
  MATCH_MODE_LEGACY: '/app/match-mode',
  FIXTURES: '/app/fixtures',

  // Testing & domain pages (legacy + current)
  GPS: '/app/gps',
  LOAD_MONITORING: '/app/load-monitoring',
  SPEED: '/app/speed',
  SPEED_TESTING: '/app/speed-testing',
  FITNESS: '/app/fitness',
  FITNESS_TESTING: '/app/fitness-testing',
  BODYCOMP: '/app/bodycomp',
  BODY_COMPOSITION: '/app/body-composition',
  METABOLIC: '/app/metabolic',
  METABOLIC_TESTING: '/app/metabolic-testing',
  COGNITIVE: '/app/cognitive',
  COGNITIVE_TESTING: '/app/cognitive-testing',

  USER_MANAGEMENT: '/app/user-management',
  SETTINGS: '/app/settings',
  SETTINGS_ACCOUNT: '/app/settings/account',
  SETTINGS_BILLING: '/app/settings/billing',
  SETTINGS_ORGANIZATION: '/app/settings/organization',
  SETTINGS_TEAM: '/app/settings/team',
  SETTINGS_NOTIFICATIONS: '/app/settings/notifications',
  SETTINGS_SQUADS: '/app/settings/squads',
  SETTINGS_USERS: '/app/settings/users',
  SETTINGS_INTEGRATIONS: '/app/settings/integrations',
  SETTINGS_PERMISSIONS: '/app/settings/permissions',
  SETTINGS_ROLE_PERMISSIONS: '/app/settings/role-permissions',
  SETTINGS_SECURITY_COMPLIANCE: '/app/settings/security-and-compliance',
  SETTINGS_DATA_EXPORT: '/app/settings/data-export',
  SETTINGS_KIOSK: '/app/settings/kiosk',
  KIOSK: (orgSlug: string) => `/kiosk/${orgSlug}`,

  STRENGTH_CONDITIONING: '/app/strength-conditioning',
  STRENGTH_CONDITIONING_SESSION_NEW: '/app/strength-conditioning/session/new',
  STRENGTH_CONDITIONING_SESSION: (id: string) => `/app/strength-conditioning/session/${id}`,
  STRENGTH_CONDITIONING_SESSION_LOG: (id: string) => `/app/strength-conditioning/session/${id}/log`,
  STRENGTH_CONDITIONING_SESSION_RUN: (id: string) => `/app/strength-conditioning/session/${id}/run`,

  // Player portal
  PLAYER_HOME: '/player/home',
  PLAYER_CHECK_IN: '/player/check-in',
  PLAYER_SCHEDULE: '/player/schedule',
  PLAYER_MESSAGES: '/player/messages',
  PLAYER_PROFILE: '/player/profile',
  PLAYER_HISTORY: '/player/history',
  PLAYER_MY_CLUB: '/player/my-club',
  PLAYER_REPORT_CONCERN: '/player/report-concern',

  // Parent portal
  PARENT_PORTAL: '/app/parent-portal',
  PARENT_HOME: '/app/parent-portal/home',
  PARENT_SCHEDULE: '/app/parent-portal/schedule',
  // PARENT_PAYMENTS removed — feature cut from product scope.
  PARENT_MESSAGES: '/app/parent-portal/messages',
  PARENT_DOCUMENTS: '/app/parent-portal/documents',
  PARENT_STAFF: '/app/parent-portal/staff',
  PARENT_CONSENTS: '/app/parent-portal/consents',

  // Legacy aliases — dead or redirect routes, kept as constants for traceability
  LEGACY_PLANNER: '/app/planner',
  LEGACY_GPS: '/app/gps',
  LEGACY_SPEED: '/app/speed',
  LEGACY_FITNESS: '/app/fitness',
  LEGACY_FITNESS_TESTING: '/app/fitness-testing',
  LEGACY_GRANTS: '/app/grants',
} as const;

export type RouteKey = keyof typeof ROUTES;
